import type { Socket } from 'socket.io-client'
import { isSessionExpiredPayload } from '@/lib/socket'

/**
 * Failsafe de transporte: se a conexão morrer com o comando em voo, a promise do
 * ack nunca assentaria. NÃO é prazo pra "esperar um erro aparecer".
 */
export const ACK_TIMEOUT_MS = 10_000

/**
 * - `ok` — o back confirmou (ou não negou);
 * - `denied` — o back recusou (escopo/sessão/payload);
 * - `unknown` — não deu pra saber (ack não voltou). Distinto de `denied` de
 *   propósito: tratar transporte instável como negativa fecharia tela legítima.
 */
export type CommandVerdict = 'ok' | 'denied' | 'unknown'

/**
 * Ack de payload `{ok, code?, message?}` — o back responde assim em TODO comando
 * desde o decorator `socket_command` (`python-orchestrator/app/sockets/command.py`).
 * `ok: false` vira `denied`, EXCETO `code: 'session_expired'` → `unknown`: o back
 * não achou o user do sid (`app/sockets/command.py:117-123`) e emite o `error`
 * que recicla a conexão ([[socket]]) — é transitório, não negativa. `internal`
 * ainda vira `denied` (o toast mente nele; o `code` não chega no veredito).
 */
function verdictFromAck(ack: unknown): CommandVerdict | null {
  if (typeof ack !== 'object' || ack === null || !('ok' in ack)) return null
  const { ok, code } = ack as { ok: unknown; code?: unknown }
  if (ok) return 'ok'
  return code === 'session_expired' ? 'unknown' : 'denied'
}

/**
 * Emite um COMANDO de socket (join de sala etc.) e devolve o veredito.
 *
 * Funciona nos dois contratos — foi o que permitiu migrar o back handler a
 * handler sem tocar em call site nenhum:
 *
 * 1. **Ack de payload** (`{ok, code}`) — o contrato do back HOJE, em todo comando.
 *    Lê o veredito direto. Determinístico.
 * 2. **Legado** (handler retornava `None` → ack vazio nos dois desfechos) — cai
 *    na ORDEM dos pacotes: a negativa faz `await safe_emit('error', ..., to=sid)`
 *    ANTES de retornar, e ambos descem pela mesma conexão ordenada, então o `error`
 *    precede o ack. Um `error` (não-de-sessão) visto entre o emit e o ack É a
 *    resposta a este emit.
 *
 * O fallback tem um limite conhecido: com DOIS comandos em voo ao mesmo tempo, o
 * `error` de um pode ser creditado ao outro (o canal `error` é global e não traz
 * correlação). Por isso o pedido de padronização (atendido) — no modo 1 o problema some.
 */
export async function emitCommand(
  socket: Socket,
  event: string,
  payload?: unknown,
): Promise<CommandVerdict> {
  let errorAntesDoAck = false
  const onError = (raw: unknown) => {
    // Sessão expirada é reciclagem de conexão (tratada em `lib/socket`), não
    // negativa de acesso — contar como denied seria falso positivo.
    if (isSessionExpiredPayload(raw)) return
    errorAntesDoAck = true
  }

  socket.on('error', onError)
  try {
    const emitter = socket.timeout(ACK_TIMEOUT_MS)
    const ack = payload === undefined
      ? await emitter.emitWithAck(event)
      : await emitter.emitWithAck(event, payload)

    return verdictFromAck(ack) ?? (errorAntesDoAck ? 'denied' : 'ok')
  } catch {
    return 'unknown'
  } finally {
    socket.off('error', onError)
  }
}
